import React from "react";
import { Link } from "react-router-dom";
import { Check, MapPin, Mail, Phone, Ruler, Accessibility, Wind, Sparkles, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { seo } from "../translations/seo";
import { useLanguage } from "../context/LanguageContext";

export default function Collaborer() {
  const { t, language } = useLanguage();
  const c = t.collaborer;

  return (
    <div className="min-h-screen bg-white font-jakarta">
      <Navbar />
      <Seo title={seo[language].collaborer.title} description={seo[language].collaborer.description} />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-50 via-blue-50/60 to-cyan-50/40">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#2E4DB5] mb-4">
            {c.eyebrow}
          </p>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
            {c.title}
          </h1>
          <p className="text-slate-600 text-lg leading-relaxed max-w-2xl mx-auto">
            {c.intro}
          </p>
        </div>
      </section>

      {/* Abbaye highlight */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <div className="bg-gradient-to-br from-[#2E4DB5] to-[#3B9DA5] rounded-3xl p-8 sm:p-10 text-white shadow-xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/80 mb-3">
              <Sparkles className="w-4 h-4" /> Psymed Abbaye
            </div>
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold leading-tight mb-4">
              {c.abbaye_title}
            </h2>
            <p className="text-white/90 leading-relaxed mb-5">{c.abbaye_lead}</p>
            <div className="flex items-start gap-3 bg-white/10 rounded-2xl p-4">
              <Accessibility className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
              <p className="text-sm text-white/95">{c.abbaye_pmr}</p>
            </div>
          </div>

          {/* Features grid */}
          <div className="mt-12">
            <h3 className="font-outfit text-xl sm:text-2xl font-bold text-slate-900 mb-6">
              {c.features_title}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {c.features.map((f, i) => (
                <div key={i} className="flex items-start gap-3 bg-slate-50 rounded-2xl p-4">
                  <Check className="w-5 h-5 text-[#3B9DA5] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="mt-12">
            <h3 className="font-outfit text-xl sm:text-2xl font-bold text-slate-900 mb-2">
              {c.pricing_title}
            </h3>
            <p className="text-sm text-slate-500 mb-6">{c.pricing_note}</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="border-2 border-slate-100 rounded-3xl p-6 hover:border-[#3B9DA5] transition-colors">
                <p className="text-sm text-slate-500 uppercase tracking-wide font-semibold mb-2">{c.half_day}</p>
                <p className="font-outfit text-3xl font-bold bg-gradient-to-r from-[#2E4DB5] to-[#3B9DA5] bg-clip-text text-transparent">
                  {c.half_day_price}
                </p>
              </div>
              <div className="border-2 border-[#3B9DA5] bg-blue-50/40 rounded-3xl p-6 relative">
                <p className="text-sm text-slate-500 uppercase tracking-wide font-semibold mb-2">{c.full_day}</p>
                <p className="font-outfit text-3xl font-bold bg-gradient-to-r from-[#2E4DB5] to-[#3B9DA5] bg-clip-text text-transparent">
                  {c.full_day_price}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cinquantenaire section */}
      <section className="py-16 bg-slate-50/60">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm">
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              {c.cinquantenaire_title}
            </h2>
            <p className="text-slate-600 leading-relaxed">{c.cinquantenaire_body}</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {c.cta_title}
          </h2>
          <p className="text-slate-600 mb-8">{c.cta_body}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`mailto:${c.contact_email}`}
              className="gradient-bg text-white font-semibold px-8 py-4 rounded-full hover:opacity-90 inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
              data-testid="collab-email"
            >
              <Mail className="w-4 h-4" /> {c.contact_email}
            </a>
            <a
              href={`tel:${c.contact_phone.replace(/\s/g, "")}`}
              className="bg-white text-[#2E4DB5] font-semibold px-8 py-4 rounded-full border border-slate-200 hover:border-[#2E4DB5] hover:bg-slate-50 inline-flex items-center justify-center gap-2"
              data-testid="collab-phone"
            >
              <Phone className="w-4 h-4" /> {c.contact_phone}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
